import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { Room as RoomType } from '../api/types'
import type { ChatMessage } from '../webrtc/types'
import { useLocalMedia } from '../webrtc/useLocalMedia'
import { useMesh } from '../webrtc/useMesh'
import { useAuth } from './AuthContext'
import { useToast } from './ToastContext'
import { VideoTile } from '../components/VideoTile'

interface SessionInit {
  slug: string
  room: RoomType
  ticket: string
  iceServers: RTCIceServer[]
  initialMessages?: ChatMessage[]
}

type ActiveMedia = ReturnType<typeof useLocalMedia>
type ActiveMesh = ReturnType<typeof useMesh>

interface ActiveSessionValue {
  session: SessionInit
  media: ActiveMedia
  mesh: ActiveMesh
}

interface RoomSessionApi {
  active: SessionInit | null
  enterSession: (data: SessionInit) => void
  leaveSession: () => void
}

const ApiCtx = createContext<RoomSessionApi | null>(null)
const ActiveCtx = createContext<ActiveSessionValue | null>(null)

export function useRoomSessionApi(): RoomSessionApi {
  const v = useContext(ApiCtx)
  if (!v) throw new Error('useRoomSessionApi must be used within RoomSessionProvider')
  return v
}

export function useActiveSession(): ActiveSessionValue | null {
  return useContext(ActiveCtx)
}

export function RoomSessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionInit | null>(null)

  const enterSession = useCallback((data: SessionInit) => {
    setSession(data)
  }, [])

  const leaveSession = useCallback(() => {
    setSession(null)
  }, [])

  const apiValue = useMemo<RoomSessionApi>(
    () => ({ active: session, enterSession, leaveSession }),
    [session, enterSession, leaveSession],
  )

  return (
    <ApiCtx.Provider value={apiValue}>
      {session ? (
        <ActiveSessionInner key={session.slug} session={session} onLeave={leaveSession}>
          {children}
        </ActiveSessionInner>
      ) : (
        children
      )}
    </ApiCtx.Provider>
  )
}

function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
}

function ActiveSessionInner({
  session,
  onLeave,
  children,
}: {
  session: SessionInit
  onLeave: () => void
  children: ReactNode
}) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()

  const media = useLocalMedia()
  const mesh = useMesh({
    slug: session.slug,
    ticket: session.ticket,
    iceServers: session.iceServers,
    localStream: media.stream,
    displayName: user?.displayName ?? 'Ich',
    initialMessages: session.initialMessages,
    onKicked: () => {
      toast.push('Du wurdest aus dem Raum entfernt', 'error')
      onLeave()
      navigate('/', { replace: true })
    },
  })

  // End the call when the phone goes to standby / is locked. visibilitychange
  // fires with state 'hidden' on mobile when the screen turns off. A short
  // grace window prevents ending on brief notification-panel checks; a phone
  // pauses JS while locked so a pending timer fires on unlock — which is
  // exactly when we want to confirm the session is over.
  const hideTimerRef = useRef<number | null>(null)
  useEffect(() => {
    if (!isMobileDevice()) return
    const GRACE_MS = 30_000
    function clearTimer() {
      if (hideTimerRef.current !== null) {
        window.clearTimeout(hideTimerRef.current)
        hideTimerRef.current = null
      }
    }
    function onVisibility() {
      if (document.visibilityState === 'hidden') {
        clearTimer()
        hideTimerRef.current = window.setTimeout(() => {
          toast.push('Verbindung wegen Standby beendet', 'error')
          media.stopAllTracks()
          onLeave()
        }, GRACE_MS)
      } else {
        clearTimer()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      clearTimer()
    }
  }, [media, onLeave, toast])

  const value = useMemo<ActiveSessionValue>(() => ({ session, media, mesh }), [session, media, mesh])

  return (
    <ActiveCtx.Provider value={value}>
      {children}
      <FloatingPip />
    </ActiveCtx.Provider>
  )
}

function FloatingPip() {
  const active = useActiveSession()
  const navigate = useNavigate()
  const location = useLocation()

  if (!active) return null
  const onRoomRoute = location.pathname === `/rooms/${active.session.slug}`
  if (onRoomRoute) return null

  const { media, mesh, session } = active
  const screener = mesh.peers.find((p) => p.info.screen)
  const firstRemote = mesh.peers.find((p) => !!p.stream)
  const showPeer = screener ?? firstRemote
  const stream = showPeer?.stream ?? media.stream
  const displayName = showPeer?.info.displayName ?? 'Ich'

  return (
    <div
      className="floating-pip"
      role="button"
      tabIndex={0}
      title={`Zurück zu „${session.room.name}"`}
      onClick={() => navigate(`/rooms/${session.slug}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          navigate(`/rooms/${session.slug}`)
        }
      }}
    >
      <VideoTile
        stream={stream}
        displayName={displayName}
        muted={!showPeer}
        mirrored={!showPeer && !media.screenSharing}
        audioEnabled={showPeer ? showPeer.info.audio : media.audioEnabled}
        videoEnabled={showPeer ? showPeer.info.video : media.videoEnabled}
        audioOutputId={media.selectedAudioOut}
      />
      <div className="floating-pip-bar">
        <span className="floating-pip-name">{session.room.name}</span>
        <span className={`conn ${mesh.connected ? 'on' : 'off'}`}>
          {mesh.connected ? '● Live' : '○'}
        </span>
      </div>
    </div>
  )
}
